/*
 * LoftiePM checkout prompt (US supplement-gwp).
 *
 * The auto-added LoftiePM subscription needs the shopper's consent tick before checkout.
 * Without it, gwp-cart-validation-rs (shopify-loftie-systems-discount #22) refuses the WHOLE
 * order, clock included, and the shopper has to come back to the cart. This asks the question
 * at the Checkout click instead:
 *
 *   Keep your free first bottle?  [Yes, keep it and subscribe]  [No thanks, just the clock]
 *
 * "Yes" records lpm_sub_consent='yes' (the same active yes as the cart tick); "No thanks"
 * removes the subscription line. Either way the shopper lands on checkout in one click and the
 * clock is never blocked. #22 stays as the backstop for anything that skips this (direct
 * /checkout URL, a failed write).
 *
 * Callers (drawer: assets/vue-checkout-btn.js, cart page: sections/main-cart.liquid) check
 * LoftiePMGate.hasAutoSub(cart) synchronously first, so carts without the subscription never
 * wait on this.
 */
(function () {
  if (window.LoftiePMGate) return;

  var LOFTIEPM_PRODUCT_ID = 9761498038519;

  function subLines(cart) {
    return ((cart && cart.items) || []).filter(function (i) {
      return i.product_id === LOFTIEPM_PRODUCT_ID && i.selling_plan_allocation
        && i.properties && i.properties._auto_added === 'true';
    });
  }

  function consented(cart) {
    return !!(cart && cart.attributes && cart.attributes.lpm_sub_consent === 'yes');
  }

  function track(choice) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'loftiepm_checkout_prompt', loftiepm_choice: choice });
    } catch (e) { /* analytics must never block checkout */ }
  }

  // Writes give up after 10s so a hung request can't leave the prompt stuck with its buttons disabled.
  function post(url, body) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl && setTimeout(function () { ctrl.abort(); }, 10000);
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl ? ctrl.signal : undefined
    }).finally(function () { clearTimeout(timer); }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  var STYLE =
    '.lpm-gate{border:0;padding:0;border-radius:12px;max-width:420px;width:calc(100% - 32px);background:#fbf7ed;color:#191919;box-shadow:0 12px 40px rgba(0,0,0,.25)}' +
    '.lpm-gate::backdrop{background:rgba(0,0,0,.45)}' +
    '.lpm-gate__inner{padding:24px 20px 20px;position:relative}' +
    '.lpm-gate__close{position:absolute;top:10px;right:10px;background:none;border:0;padding:6px;cursor:pointer;color:#191919;line-height:0}' +
    '.lpm-gate__title{font-size:20px;line-height:1.25;margin:0 28px 8px 0;font-weight:600}' +
    '.lpm-gate__terms{font-size:14px;line-height:1.45;margin:0 0 16px}' +
    '.lpm-gate__terms a{text-decoration:underline}' +
    '.lpm-gate__actions{display:flex;flex-direction:column;gap:10px}' +
    '.lpm-gate__actions .button{margin:0}' +
    // Match the drawer's Checkout button (rgb(82,45,39), white text) so "keep" reads as the main choice.
    '.lpm-gate .lpm-gate__keep{background:rgb(82,45,39);color:#fff;border:1px solid rgb(82,45,39);border-radius:4px}' +
    '.lpm-gate .lpm-gate__keep:hover{background:rgb(62,33,28)}' +
    '.lpm-gate__error{font-size:13px;color:#b3261e;margin:12px 0 0}';

  var dialog = null;
  var inFlight = null;

  function build() {
    var style = document.createElement('style');
    style.textContent = STYLE;
    document.head.appendChild(style);

    dialog = document.createElement('dialog');
    dialog.className = 'lpm-gate';
    dialog.setAttribute('aria-labelledby', 'lpm-gate-title');
    dialog.innerHTML =
      '<div class="lpm-gate__inner">' +
        '<button type="button" class="lpm-gate__close" data-lpm="close" aria-label="Back to cart">' +
          '<svg viewBox="0 0 20 20" width="20" height="20" fill="currentColor" aria-hidden="true" focusable="false"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/></svg>' +
        '</button>' +
        '<p id="lpm-gate-title" class="lpm-gate__title">Keep your free first bottle of LoftiePM?</p>' +
        '<p class="lpm-gate__terms">Free first bottle with your clock, then $23.99/mo + tax. Renews monthly until you cancel. Cancel anytime. ' +
          '<a href="/policies/subscription-policy" target="_blank" rel="noopener">Subscription terms</a></p>' +
        '<div class="lpm-gate__actions">' +
          '<button type="button" class="button button--full lpm-gate__keep" data-lpm="keep">Yes, keep it and subscribe</button>' +
          '<button type="button" class="button button--outline button--full" data-lpm="decline">No thanks, just the clock</button>' +
        '</div>' +
        '<p class="lpm-gate__error" data-lpm="error" role="alert" hidden>Something went wrong. Please try again.</p>' +
      '</div>';
    document.body.appendChild(dialog);
  }

  // Resolves true when the shopper may proceed to checkout (consent recorded, or the
  // subscription removed), false when they closed the prompt to stay in the cart.
  function ask(lines) {
    if (!dialog) build();
    var err = dialog.querySelector('[data-lpm="error"]');
    var buttons = dialog.querySelectorAll('button');
    err.hidden = true;

    return new Promise(function (resolve) {
      var busy = false;

      function setBusy(b) {
        busy = b;
        buttons.forEach(function (btn) { btn.disabled = b; });
      }

      var done = false;
      function finish(result) {
        if (done) return;
        done = true;
        dialog.removeEventListener('click', onClick);
        dialog.removeEventListener('cancel', onCancel);
        dialog.removeEventListener('close', onClose);
        if (dialog.open) dialog.close();
        resolve(result);
      }

      // The browser can close a modal without a cancel event (e.g. Chrome's repeated-Esc rule).
      // Treat any close we didn't make as "back to the cart".
      function onClose() {
        if (!done) { track('dismissed'); finish(false); }
      }

      function fail(e) {
        console.warn('[LoftiePMGate]', e);
        setBusy(false);
        err.hidden = false;
      }

      function onCancel(e) {
        // Esc: back to the cart, unless a write is in flight.
        e.preventDefault();
        if (!busy) { track('dismissed'); finish(false); }
      }

      function onClick(e) {
        if (busy) return;
        // A click on the backdrop lands on the <dialog> itself.
        var el = e.target.closest('[data-lpm]');
        var action = e.target === dialog ? 'close' : (el && el.getAttribute('data-lpm'));
        if (action === 'close') { track('dismissed'); finish(false); return; }

        if (action === 'keep') {
          setBusy(true);
          post('/cart/update.js', { attributes: { lpm_sub_consent: 'yes', lpm_sub_consent_source: 'checkout_prompt' } }).then(function (cart) {
            if (!consented(cart)) throw new Error('consent not recorded');
            track('keep');
            finish(true);
          }).catch(fail);
        } else if (action === 'decline') {
          setBusy(true);
          var updates = {};
          lines.forEach(function (l) { updates[l.key] = 0; });
          post('/cart/update.js', { updates: updates, attributes: { lpm_sub_consent: '', lpm_sub_consent_source: '' } }).then(function (cart) {
            if (subLines(cart).length) throw new Error('subscription not removed');
            track('decline');
            finish(true);
          }).catch(fail);
        }
      }

      dialog.addEventListener('click', onClick);
      dialog.addEventListener('cancel', onCancel);
      dialog.addEventListener('close', onClose);
      dialog.showModal();
      // Focus the close button, never the consent button: an auto-repeating Enter must not consent.
      dialog.querySelector('[data-lpm="close"]').focus();
    });
  }

  window.LoftiePMGate = {
    // Synchronous pre-check against the caller's local cart copy: is the auto-added subscription
    // there at all? False means "don't intercept". Consent itself is judged by check() on the live
    // cart, because the local copy can be stale either way (a box just ticked, or just unticked).
    hasAutoSub: function (cart) {
      return subLines(cart).length > 0;
    },

    // Re-reads the live cart, then asks only if the subscription is there without consent. Resolves true to proceed to checkout, false to stay in the cart. If the cart
    // can't be read, proceeds: #22 still refuses an unconsented subscription at checkout.
    check: function () {
      // One prompt at a time: a second click while the first check is in flight gets the same answer.
      if (inFlight) return inFlight;
      var pending = window.__lpmConsentPending || Promise.resolve();
      inFlight = pending.catch(function () {}).then(function () {
        return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      }).then(function (r) { return r.json(); }).then(function (cart) {
        var lines = subLines(cart);
        if (!lines.length || consented(cart)) return true;
        return ask(lines);
      }).catch(function (e) {
        console.warn('[LoftiePMGate] cart read failed; server gate still applies', e);
        return true;
      }).then(function (result) { inFlight = null; return result; });
      return inFlight;
    }
  };
})();
